/**
 * Room Type Configuration Component
 * Allows Company Admin to set default amenities and guest capacity for room types at hotel level
 */
import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Layers, Users, Sparkles, Save, X, Settings, Plus } from 'lucide-react';

export default function RoomTypeConfigDialog({ 
  open, 
  onOpenChange, 
  hotelId, 
  hotelName 
}) {
  const [loading, setLoading] = useState(true);
  const [roomTypes, setRoomTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [configs, setConfigs] = useState([]);
  
  // Form state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [formData, setFormData] = useState({
    default_amenity_ids: [],
    default_adult_capacity: '',
    max_adult_capacity: '',
    default_child_capacity: '',
    max_child_capacity: ''
  });

  useEffect(() => {
    if (open && hotelId) {
      loadData();
    }
  }, [open, hotelId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [roomTypesRes, amenitiesRes, configsRes] = await Promise.all([
        api.get('/room-types'),
        api.get('/amenities'),
        api.get(`/companies/hotels/${hotelId}/room-type-config`).catch(() => ({ data: [] }))
      ]);
      setRoomTypes(roomTypesRes.data.filter(rt => rt.is_active));
      setAmenities(amenitiesRes.data.filter(a => a.is_active));
      setConfigs(configsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load room type configurations');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (roomType) => {
    setSelectedRoomType(roomType);
    // Find existing config for this room type
    const existingConfig = configs.find(c => c.room_type_id === roomType.id);
    if (existingConfig) {
      setFormData({
        default_amenity_ids: existingConfig.default_amenity_ids || [],
        default_adult_capacity: existingConfig.default_adult_capacity?.toString() || '',
        max_adult_capacity: existingConfig.max_adult_capacity?.toString() || '',
        default_child_capacity: existingConfig.default_child_capacity?.toString() || '',
        max_child_capacity: existingConfig.max_child_capacity?.toString() || ''
      });
    } else {
      setFormData({
        default_amenity_ids: [],
        default_adult_capacity: '',
        max_adult_capacity: '',
        default_child_capacity: '',
        max_child_capacity: ''
      });
    }
    setEditDialogOpen(true);
  };

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      default_amenity_ids: prev.default_amenity_ids.includes(amenityId)
        ? prev.default_amenity_ids.filter(id => id !== amenityId)
        : [...prev.default_amenity_ids, amenityId]
    }));
  };

  const handleSave = async () => {
    if (!selectedRoomType) return;
    
    try {
      await api.put(`/companies/hotels/${hotelId}/room-type-config`, {
        room_type_id: selectedRoomType.id,
        default_amenity_ids: formData.default_amenity_ids,
        default_adult_capacity: formData.default_adult_capacity ? parseInt(formData.default_adult_capacity) : null,
        max_adult_capacity: formData.max_adult_capacity ? parseInt(formData.max_adult_capacity) : null,
        default_child_capacity: formData.default_child_capacity ? parseInt(formData.default_child_capacity) : null,
        max_child_capacity: formData.max_child_capacity ? parseInt(formData.max_child_capacity) : null
      });
      toast.success(`Configuration saved for ${selectedRoomType.name}`);
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const getConfigForRoomType = (roomTypeId) => {
    return configs.find(c => c.room_type_id === roomTypeId);
  };

  const getAmenityNames = (amenityIds) => {
    if (!amenityIds || amenityIds.length === 0) return 'None';
    return amenityIds.map(id => {
      const amenity = amenities.find(a => a.id === id);
      return amenity?.name || id;
    }).join(', ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-orange-500" />
            Room Type Configuration
          </DialogTitle>
          <DialogDescription>
            Set default amenities and guest capacity for room types at {hotelName || 'this hotel'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading configurations...</div>
        ) : (
          <div className="space-y-4">
            {roomTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Layers className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p>No room types available. Please add room types first.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {roomTypes.map((roomType) => {
                  const config = getConfigForRoomType(roomType.id);
                  const hasConfig = !!config;
                  
                  return (
                    <Card key={roomType.id} className={`border ${hasConfig ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Layers className="h-4 w-4 text-orange-500" />
                              <span className="font-medium">{roomType.name}</span>
                              {hasConfig && (
                                <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200">
                                  Configured
                                </Badge>
                              )}
                            </div>
                            
                            {hasConfig && (
                              <div className="mt-2 text-sm text-gray-600 grid grid-cols-2 gap-2">
                                <div className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  <span>Adults: {config.default_adult_capacity || '-'} (max: {config.max_adult_capacity || '-'})</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  <span>Children: {config.default_child_capacity || '-'} (max: {config.max_child_capacity || '-'})</span>
                                </div>
                                <div className="col-span-2 flex items-center gap-1">
                                  <Sparkles className="h-3 w-3" />
                                  <span className="truncate">Amenities: {getAmenityNames(config.default_amenity_ids)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openEditDialog(roomType)}
                            className="ml-4"
                          >
                            {hasConfig ? 'Edit' : 'Configure'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* Edit Configuration Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configure {selectedRoomType?.name}</DialogTitle>
            <DialogDescription>
              Set default values for rooms of this type
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Guest Capacity Section */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-orange-500" />
                Guest Capacity
              </Label>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Default Adults</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.default_adult_capacity}
                    onChange={(e) => setFormData({...formData, default_adult_capacity: e.target.value})}
                    placeholder="2"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Max Adults</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_adult_capacity}
                    onChange={(e) => setFormData({...formData, max_adult_capacity: e.target.value})}
                    placeholder="4"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Default Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.default_child_capacity}
                    onChange={(e) => setFormData({...formData, default_child_capacity: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Max Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.max_child_capacity}
                    onChange={(e) => setFormData({...formData, max_child_capacity: e.target.value})}
                    placeholder="2"
                  />
                </div>
              </div>
            </div>

            {/* Default Amenities Section */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-orange-500" />
                Default Amenities
              </Label>
              
              <ScrollArea className="h-32 border rounded-lg p-2">
                <div className="flex flex-wrap gap-2">
                  {amenities.map((amenity) => {
                    const isSelected = formData.default_amenity_ids.includes(amenity.id);
                    return (
                      <label
                        key={amenity.id}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer text-xs ${
                          isSelected
                            ? 'bg-orange-100 border border-orange-400 text-orange-700'
                            : 'bg-gray-50 border border-transparent hover:bg-orange-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleAmenity(amenity.id)}
                          className="hidden"
                        />
                        <Sparkles className="h-3 w-3" />
                        {amenity.name}
                      </label>
                    );
                  })}
                </div>
              </ScrollArea>
              <p className="text-xs text-gray-500">
                These amenities will be pre-selected when creating new rooms of this type
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
